import { Link } from 'react-router-dom';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import type { ProjectWithCustomer } from '../types';

interface ProjectCardProps {
  project: ProjectWithCustomer;
}

export function ProjectCard({ project }: ProjectCardProps) {
  // Format date for display (handles timezone correctly)
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';
  
  // Parse as local date to avoid timezone shift
  // dateString format: "2025-12-16"
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  
  return date.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

  return (
    <Link
      to={`/projects/${project.id}`}
      className="block bg-white rounded-xl border border-secondary-200 p-5 
                 hover:border-primary-300 hover:shadow-md transition-all duration-200"
    >
      {/* Header Row */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-secondary-900 truncate">
            {project.customer_name}
          </h3>
          <p className="text-sm text-secondary-500 truncate">
            {project.project_name}
          </p>
        </div>
        <ProjectStatusBadge status={project.status} size="sm" />
      </div>

      {/* Details */}
      <div className="space-y-1 text-sm text-secondary-600">
        {/* Site Location (fallback to customer billing address) */}
        {(project.site_street_address || project.customer_billing_city) && (
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {project.site_street_address || project.customer_billing_street_address}, {project.site_province || project.customer_billing_province}
          </span>
        )}

        {/* Date */}
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {project.start_date ? formatDate(project.start_date) : <span className="text-secondary-400 italic">No date scheduled</span>}
        </span>
      </div>

      {/* Customer Email */}
      {project.customer_email && (
        <p className="text-sm text-secondary-400 mt-2 truncate">
          {project.customer_email}
        </p>
      )}
    </Link>
  );
}